import { formatUnits, maxUint256 } from 'viem'

import { getCollectionMetadataLookupKey } from '~/components/txn-display/state-change/hooks/useNFTCollectionMetadata'
import {
  ApprovalStateChangeItem,
  BuildStateChangeItemsArgs,
  ERC20StateChangeItem,
  NFTCollectionStateChangeItem,
  SortableStateChangeItem,
  StateChangeItem,
} from '~/components/txn-display/state-change/types'
import { getTokenMetadataLookupKey } from '~/components/txn-display/state-change/utils/getTokenMetadataLookupKey'
import { sortStateChangeItems } from '~/components/txn-display/state-change/utils/sortStateChangeItems'
import { TokenMetadataWithPrice } from '~/hooks/useTokenMetadata'

import type { ApprovalChange } from '~/components/txn-display/state-change/types'

const APPROVAL_DUST_HIDE_USD_THRESHOLD = 0.01

const shouldHideApproval = ({
  approval,
  tokenOutflowTotals,
  metadata,
  hiddenRemainderAmount = 0n,
}: {
  approval: ApprovalChange
  tokenOutflowTotals?: Record<string, Record<string, bigint>>
  metadata?: TokenMetadataWithPrice
  hiddenRemainderAmount?: bigint
}) => {
  if (approval.amount === 0n) return true
  if (approval.amount === maxUint256) return false

  const observedOutflowAmount =
    tokenOutflowTotals?.[approval.owner]?.[approval.token] ?? 0n
  const positiveHiddenRemainderAmount =
    hiddenRemainderAmount > 0n ? hiddenRemainderAmount : 0n
  const deltaUsdValue = computeUsdValue(
    absBigInt(approval.amount - observedOutflowAmount),
    metadata,
  )
  const adjustedTargetAmount =
    observedOutflowAmount + positiveHiddenRemainderAmount
  const adjustedDeltaAmount = approval.amount - adjustedTargetAmount
  const adjustedDeltaUsdValue = computeUsdValue(
    absBigInt(adjustedDeltaAmount),
    metadata,
  )

  // Approval amount exactly matches the raw outflow (standard swap)
  const isExactMatch = approval.amount === observedOutflowAmount
  // Approval slightly exceeds outflow — some routers approve a max input
  // then spend slightly less, leaving a sub-cent dust difference
  const isDustDifference =
    approval.amount > observedOutflowAmount &&
    deltaUsdValue !== undefined &&
    deltaUsdValue < APPROVAL_DUST_HIDE_USD_THRESHOLD
  // The excess over raw outflow matches a hidden same-token remainder
  // (e.g. user swaps from Earn USDC — router returns a small USDC amount
  // that we've already hidden as a same-token state change)
  const isHiddenRemainderMatch =
    positiveHiddenRemainderAmount > 0n &&
    (approval.amount === adjustedTargetAmount ||
      (adjustedDeltaAmount > 0n &&
        adjustedDeltaUsdValue !== undefined &&
        adjustedDeltaUsdValue < APPROVAL_DUST_HIDE_USD_THRESHOLD))

  return isExactMatch || isDustDifference || isHiddenRemainderMatch
}

export const buildStateChangeItems = ({
  combinedResult,
  bridgeStateChanges,
  metadataLookup,
  nftCollectionMetadataLookup,
  stateChangesToHide,
}: BuildStateChangeItemsArgs): StateChangeItem[] => {
  const tokenOutflowTotals = combinedResult.tokenOutflowTotals ?? {}

  const fungibleSource = [
    ...combinedResult.stateChanges,
    ...(bridgeStateChanges ?? []),
  ]

  const tokenItems: SortableStateChangeItem[] = fungibleSource
    .filter((change) => !stateChangesToHide?.[change.address]?.[change.token])
    .filter((change) => change.amount !== 0n)
    .map((change, index) => {
      const metadata = metadataLookup.get(
        getTokenMetadataLookupKey(change.chainId, change.token),
      )
      const absoluteAmount = absBigInt(change.amount)
      const tokenAmount = computeTokenAmount(absoluteAmount, metadata)

      const payload: ERC20StateChangeItem = {
        id: `erc20-${change.address}-${change.token}-${change.chainId}-${index}`,
        state_change_type: 'erc-20',
        address: change.address,
        chainId: change.chainId,
        direction: change.amount >= 0n ? 'inflow' : 'outflow',
        token: change.token,
        amount: change.amount,
        tokenMetadata: metadata,
      }

      const usdValue =
        change.historicalUsdValue ?? computeUsdValue(absoluteAmount, metadata)

      payload.usdValue = usdValue

      return {
        payload,
        tokenAmountSortValue: tokenAmount,
        absoluteAmount,
        categoryRank: payload.direction === 'outflow' ? 1 : 2,
        collectionPriority: 1,
      }
    })

  const approvalItems: SortableStateChangeItem[] =
    combinedResult.approvalChanges
      .filter((approval) => {
        const metadata = metadataLookup.get(
          getTokenMetadataLookupKey(approval.chainId, approval.token),
        )
        const hiddenRemainderAmount = stateChangesToHide?.[approval.owner]?.[
          approval.token
        ]
          ? (combinedResult.stateChanges.find(
              (change) =>
                change.address === approval.owner &&
                change.token === approval.token,
            )?.amount ?? 0n)
          : 0n

        return !shouldHideApproval({
          approval,
          tokenOutflowTotals,
          metadata,
          hiddenRemainderAmount,
        })
      })
      .map((approval, index) => {
        const metadata = metadataLookup.get(
          getTokenMetadataLookupKey(approval.chainId, approval.token),
        )
        const absoluteAmount = absBigInt(approval.amount)
        const tokenAmount = computeTokenAmount(absoluteAmount, metadata)

        const payload: ApprovalStateChangeItem = {
          id: `approval-${approval.owner}-${approval.spender}-${approval.token}-${index}`,
          state_change_type: 'approval',
          address: approval.owner,
          chainId: approval.chainId,
          token: approval.token,
          owner: approval.owner,
          spender: approval.spender,
          amount: approval.amount,
          isUnlimited: approval.amount === maxUint256,
          tokenMetadata: metadata,
        }

        return {
          payload,
          usdValue: computeUsdValue(absoluteAmount, metadata),
          tokenAmountSortValue: tokenAmount,
          absoluteAmount,
          categoryRank: 0,
          collectionPriority: 1,
        }
      })

  const nftItems: SortableStateChangeItem[] = combinedResult.nftStateChanges
    .filter((change) => !stateChangesToHide?.[change.address]?.[change.token])
    .map((change, index) => {
      const metadata = metadataLookup.get(
        getTokenMetadataLookupKey(change.chainId, change.token),
      )

      // Get collection metadata for this NFT contract
      const collectionMetadata = nftCollectionMetadataLookup?.get(
        getCollectionMetadataLookupKey(change.chainId, change.token),
      )

      const payload: NFTCollectionStateChangeItem = {
        id: `nft-${change.address}-${change.token}-${change.tokenId.toString()}-${change.direction}-${index}`,
        state_change_type:
          change.tokenType === 'ERC721' ? 'erc-721' : 'erc-1155',
        address: change.address,
        chainId: change.chainId,
        direction: change.direction,
        token: change.token,
        tokenId: change.tokenId,
        amount: change.amount,
        tokenMetadata: metadata,
        collectionMetadata,
      }

      return {
        payload,
        absoluteAmount: change.amount,
        tokenAmountSortValue: toSafeNumber(change.amount),
        categoryRank: payload.direction === 'outflow' ? 1 : 2,
        collectionPriority: 0,
      }
    })

  return sortStateChangeItems([...approvalItems, ...tokenItems, ...nftItems])
}

export const computeUsdValue = (
  amount: bigint,
  metadata?: TokenMetadataWithPrice,
) => {
  if (metadata?.price === undefined) return undefined
  const tokenAmount = computeTokenAmount(amount, metadata)
  if (tokenAmount === undefined) return undefined
  const usdValue = Math.abs(tokenAmount * metadata.price)
  return Number.isFinite(usdValue) ? usdValue : undefined
}

const absBigInt = (value: bigint) => (value < 0n ? value * -1n : value)

const toSafeNumber = (value: bigint) => {
  const maxSafe = BigInt(Number.MAX_SAFE_INTEGER)
  if (value > maxSafe) return undefined
  if (value < -maxSafe) return undefined
  return Number(value)
}

const computeTokenAmount = (
  amount: bigint,
  metadata?: TokenMetadataWithPrice,
) => {
  if (!metadata) return undefined
  const parsedAmount = Number(formatUnits(amount, metadata.decimals))
  return Number.isFinite(parsedAmount) ? parsedAmount : undefined
}
